// packages/shared/src/datetime.ts
//
// DateTime helpers — wraps `dayjs` so we have a single consistent display
// format across portals (admin / merchant / vendor) per AC-28. the
// canonical display is `DD/MM/YYYY HH:mm` (Vietnamese locale convention),
// independent of the host's locale; locale-aware formatting is opt-in via
// the `locale` argument.
//
// Storage convention: all timestamps stored UTC in DB. Display layer
// converts to local timezone via `toLocalString`. Helpers here are stateless
// — no global timezone configuration so this module can be tree-shaken into
// FE bundles without side effects.
//
// FIX-13 boundary: NO Fastify / Mongoose imports.
import dayjs from 'dayjs';
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
/** Canonical display format: `DD/MM/YYYY HH:mm` (24h). */
export const DEFAULT_DATETIME_FORMAT = 'DD/MM/YYYY HH:mm';
/** Date-only display variant (no time component). */
export const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Format a date/time value using the canonical display format.
 *
 * @example
 *   formatDateTime('2026-04-27T05:30:00Z')               // "27/04/2026 12:30" (UTC+7)
 *   formatDateTime('2026-04-27T05:30:00Z', 'DD MMM YYYY') // "27 Apr 2026"
 *   formatDateTime(null)                                 // ''
 *   formatDateTime(undefined)                            // ''
 */
export function formatDateTime(input, format = DEFAULT_DATETIME_FORMAT) {
    if (input === null || input === undefined)
        return '';
    const d = dayjs(input);
    if (!d.isValid())
        return '';
    return d.format(format);
}
/**
 * Date-only convenience wrapper.
 *
 * @example
 *   formatDate('2026-04-27T05:30:00Z') // "27/04/2026"
 */
export function formatDate(input) {
    return formatDateTime(input, DEFAULT_DATE_FORMAT);
}
/**
 * Convert any input (UTC ISO, `Date`, epoch ms) into an ISO 8601 string in
 * UTC. Returns `null` for invalid input. Useful for normalizing user-entered
 * dates before persisting.
 */
export function toUtcIso(input) {
    if (input === null || input === undefined)
        return null;
    const d = dayjs(input);
    if (!d.isValid())
        return null;
    return d.toISOString();
}
/**
 * Convert any input to a JavaScript `Date` representing the same instant in
 * the host runtime's local timezone. Returns `null` on invalid input.
 *
 * Note: `Date` objects are timezone-agnostic internally; this is a
 * convenience for callers that want to feed `<input type="datetime-local">`
 * (which expects local-naive ISO strings — see `toLocalIsoNaive`).
 */
export function toLocalDate(input) {
    if (input === null || input === undefined)
        return null;
    const d = dayjs(input);
    if (!d.isValid())
        return null;
    return d.toDate();
}
/**
 * Produce a local-naive ISO string suitable for `<input type="datetime-local">`
 * — `YYYY-MM-DDTHH:mm` without timezone suffix. Returns empty string on
 * invalid input (matches HTML form-binding conventions).
 */
export function toLocalIsoNaive(input) {
    if (input === null || input === undefined)
        return '';
    const d = dayjs(input);
    if (!d.isValid())
        return '';
    return d.format('YYYY-MM-DDTHH:mm');
}
/**
 * "Now" helper as ISO 8601 UTC string. Centralized so tests can mock the
 * single source of truth (`vi.useFakeTimers()` etc.).
 */
export function nowUtcIso() {
    return dayjs().toISOString();
}
/**
 * Compute the difference in seconds between two date inputs (b - a). Returns
 * `null` if either side is invalid. Useful for "expires in X seconds" UI
 * countdowns and the OTP-resend cooldown surface.
 */
export function diffSeconds(a, b) {
    if (a == null || b == null)
        return null;
    const da = dayjs(a);
    const db = dayjs(b);
    if (!da.isValid() || !db.isValid())
        return null;
    return Math.floor((db.valueOf() - da.valueOf()) / 1000);
}
//# sourceMappingURL=datetime.js.map